<template>
  <div>
    <section class="p-box">
      <div class="p-box__body">
        <div class="c-grid">
          <div class="c-grid__items">
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__12 c-grid__item__l__12">
              <Info>
                <i18n-t keypath="pages.CompaniesMediaLibraryUpload.info.guidelines" tag="div">
                  <template #guidelines_link>
                    <a class="underline" :href="allServiceUrlConst['kcom'].photos_policy" target="_blank">{{ $t("pages.CompaniesMediaLibraryUpload.info.guidelinesLinkLabel") }}</a>
                  </template>
                </i18n-t>
              </Info>
              <MediaImage
                :images="images"
                :loading="loading"
                rules="image|postImage:720,720|size:5000|ext:jpeg,jpg,png"
                @drop-images="onDropMediaImage"
                @remove-image="onRemoveMediaImage"
              />
              <KkAcceptImageDescription />
            </div>
          </div>
        </div>
        <!-- form -->
        <div class="fields">
          <KkBox v-if="mediaLibraryServices.length" class="fields__field" border>
            <template #content>
              <KkFormLabel :text="$t('pages.CompaniesMediaLibraryUpload.service')" />
              <div class="fields__checkboxes">
                <KkCheckbox
                  v-for="service in mediaLibraryServices"
                  :id="`${service.id}_id`"
                  :key="service.id"
                  :model-value="services.includes(service.id)"
                  :label="service.name"
                  @update:model-value="onUpdateService(service.id)"
                />
              </div>
              <Info>
                {{ $t('pages.CompaniesMediaLibraryUpload.info.unchecked') }}
              </Info>
            </template>
          </KkBox>

          <template v-if="services.length">
            <KkBox class="fields__field" border>
              <template #content>
                <label for="categoryValidation" class="c-formLabel">
                  <KkTooltip
                    v-if="categoryDescription"
                    :is-visible="isShownCategoryDescription"
                    :label="$t('pages.CompaniesMediaLibraryUpload.category')"
                    font-size="12px"
                    font-weight="bold"
                    width="400px"
                    top="24px"
                    @show-tooltip="isShownCategoryDescription = true"
                    @hide-tooltip="isShownCategoryDescription = false"
                  >
                    <p class="fields__tooltip">{{ categoryDescription }}</p>
                  </KkTooltip>
                  <template v-else>
                    {{ $t('pages.CompaniesMediaLibraryUpload.category') }}
                  </template>
                </label>

                <div class="c-select">
                  <select v-model="selectedCategory">
                    <option :value="null" disabled>{{ $t('labels.placeholder.select') }}</option>
                    <option
                      v-for="category in mediaLibraryCategories"
                      :key="category.id"
                      :value="category.id"
                      :disabled="category.id === 'category_unspecified' && services.length === 1 && services[0] === 'yahoo_map'"
                    >
                      {{ category.name }}
                    </option>
                  </select>
                </div>
                <div class="selectStores__selectCategory__error">
                  <Info v-if="trySubmit && !isValidCategory" alert>
                    {{ $t('pages.CompaniesMediaLibraryUpload.notSelectedCategory') }}
                  </Info>
                </div>
              </template>
            </KkBox>

            <Loading v-show="selectStoresLoading" />
            <KkBox v-show="!selectStoresLoading" class="fields__field" border>
              <template #content>
                <p>
                  {{ $t('pages.CompaniesMediaLibraryUpload.selectedStores') }}
                  <span class="fontBold">
                    {{ storeUuids.length ? $t('labels.simpleStoresCount', { count: storeUuids.length }) : $t('messages.notSelected') }}
                  </span>
                </p>
                <SelectStores
                  :store-uuids="storeUuids"
                  :target-services="services"
                  @change-store-uuids="changeStoreUuids"
                />
              </template>
            </KkBox>
          </template>
        </div>
      </div>
      <!-- footer -->
      <footer class="p-box__footer">
        <div class="c-button c-button--right">
          <button class="c-button__item c-button__item--outline-cancel" @click="onBack">
            {{ $t('labels.cancel') }}
          </button>
          <button
            class="c-button__item c-button__item--primary"
            :class="{'c-button__item--disabled': !isValid}"
            :disabled="!isValid || loading"
            @click.prevent="onConfirm"
          >
            {{ $t('labels.uploadImages') }}
          </button>
        </div>
      </footer>
      <KkMediaLibraryUploadConfirmModal
        v-if="images.length > 0"
        :is-visible="isShownConfirmModal"
        :category-name="selectedCategoryName"
        :store-uuids="storeUuids"
        :selected-stores="selectedStores"
        :images="images"
        @on-cancel="isShownConfirmModal = false"
        @on-submit="handleSubmit"
      />
    </section>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import { mapStores } from 'pinia'
import { validate } from 'vee-validate'
import Info from '../../components/Info'
import Loading from '../../components/Loading'
import MediaImage from '../../components/MediaImage'
import SelectStores from '../../components/CompaniesMediaLibraries/SelectStores'
import { mediaLibraryCategoriesConst } from '../../modules/mediaLibraryCategoriesConst'
import { allServiceUrlConst } from '../../modules/serviceUrlConst'
import { KkMediaLibraryUploadConfirmModal } from '../../components/domains/mediaLibrary'
import { KkAcceptImageDescription } from '../../domains/mediaLibrary/components'
import { mediaLibraryServicesConst } from '../../domains/mediaLibrary/consts'
import { KkBox, KkCheckbox, KkFormLabel, KkTooltip } from '../../components/base'
import { useLocationStoreSetupsStore } from '../../domains/locationStore/stores'

export default {
  name: 'CompaniesMediaLibraryUpload',
  components: {
    KkMediaLibraryUploadConfirmModal,
    KkAcceptImageDescription,
    Info,
    Loading,
    MediaImage,
    SelectStores,
    KkCheckbox,
    KkFormLabel,
    KkBox,
    KkTooltip
  },
  data () {
    return {
      selectedCategory: null,
      trySubmit: false,
      storeUuids: [],
      services: [],
      isShownConfirmModal: false,
      isShownCategoryDescription: false,
    }
  },
  computed: {
    ...mapState({
      images: state => state.companiesMediaImage.images,
      loading: state => state.companiesMediaImage.loading || state.companiesMediaLibraries.loading,
      selectStoresLoading: state => state.companiesSearchTagGroups.loading,
    }),
    ...mapGetters({
      hasGmbStore: 'companiesCompany/hasGmbStore',
      hasYahooPlaceStore: 'companiesCompany/hasYahooPlaceStore',
      stores: 'companiesStores/stores'
    }),
    ...mapStores(useLocationStoreSetupsStore),
    mediaLibraryCategories() { return mediaLibraryCategoriesConst },
    companyUuid() { return this.$route.params.company_uuid },
    isValidCategory() { return !!this.selectedCategory },
    isValidStoreUuids() { return this.storeUuids.length > 0 },
    isNotOnlyOneCoverImage() {
      return this.selectedCategory === 'cover' && this.images.length > 1
    },
    allServiceUrlConst: () => allServiceUrlConst,
    selectedCategoryName() {
      return this.mediaLibraryCategories.find(category => category.id === this.selectedCategory)?.name ?? ''
    },
    selectedStores() {
      if (!this.stores) return []
      return this.stores
        .filter(store => this.storeUuids.includes(store.attributes.uuid))
        .map(store => ({
          name: store.attributes.name,
          uuid: store.attributes.uuid,
          services: this.services,
        }))
    },
    mediaLibraryServices() {
      const services = []
      if (this.hasGmbStore) services.push(mediaLibraryServicesConst.find((service) => service.id === 'google_map'))
      if (this.hasYahooPlaceStore) services.push(mediaLibraryServicesConst.find((service) => service.id === 'yahoo_map'))
      return services
    },
    categoryDescription() {
      if (!this.services.length) return ''
      if (this.services.length === 1) {
        if (this.services[0] === 'yahoo_map') return ''
        return this.$t('pages.CompaniesMediaLibraryUpload.googleCategoryDescription')
      }
      return this.$t('pages.CompaniesMediaLibraryUpload.bothCategoryDescription')
    },
    isValid() {
      if (!this.images.length) return false
      if (!this.services.length) return true
      return this.isValidCategory && this.isValidStoreUuids
    },
    initialYahooConnection() {
      if (!this.hasYahooPlaceStore || !this.services.includes('yahoo_map')) return false
      const setups = this.storeUuids.map((storeUuid) => this.locationStoreSetupsStore.findStoreSetup(storeUuid))
      return setups.some((setup) => setup && !setup.yahooMediaLibrary)
    }
  },
  async created() {
    const promises = []
    this.resetMediaImages()
    promises.push(this.readStores(this.companyUuid))

    if (this.hasYahooPlaceStore) {
      promises.push(this.locationStoreSetupsStore.readStoreSetups(this.companyUuid))
    }

    await Promise.all(promises)
  },
  methods: {
    ...mapActions({
      bulkCreate: 'companiesMediaLibraries/bulkCreate',
      resetMediaImages: 'companiesMediaImage/resetMediaImages',
      uploadMediaImages: 'companiesMediaImage/uploadMediaImages',
      deleteMediaImage: 'companiesMediaImage/deleteMediaImage',
      readStores: 'companiesStores/readStores'
    }),
    ...mapMutations({
      setFormValues: 'companiesMediaLibraries/setFormValues',
    }),
    async onConfirm() {
      this.trySubmit = true
      // カバー写真バリデーション
      if (this.selectedCategory === 'cover') {
        if (this.isNotOnlyOneCoverImage) {
          alert(this.$t('models.mediaLibraryStore.validation.isNotOnlyOneCoverImage'))
          return
        }
        const { valid, errors } = await validate(this.images[0].url, 'gmbCoverImage')
        if (!valid) {
          alert(errors[0])
          return
        }
      }

      this.isShownConfirmModal = true
    },
    async handleSubmit() {
      if (!this.initialYahooConnection || window.confirm(this.$t('models.mediaLibraryUpload.initialYahooConnection'))) {
        const payload = {
          media_image_ids: this.images.map(image => image.id),
          store_uuids: this.storeUuids,
          gmb_category: this.selectedCategory,
          services: this.services,
        }

        const isSuccess = await this.bulkCreate(payload)
        if (!isSuccess) return

        this.isShownConfirmModal = false
        this.onBack()
      } else {
        this.isShownConfirmModal = false
      }
    },
    onDropMediaImage(images) {
      if(this.loading) return

      this.uploadMediaImages(images)
    },
    onUpdateService(value) {
      if (this.services.includes(value)) {
        this.services = this.services.filter((service) => service !== value)

        if (!this.services.length) {
          this.selectedCategory = null
          this.storeUuids = []
        }
      } else {
        this.services = [...this.services, value]
      }

      if (!this.services.length || (this.services.length === 1 && this.services[0] === 'yahoo_map' && this.selectedCategory === 'category_unspecified')) {
        this.selectedCategory = null
      }
    },
    onRemoveMediaImage(imageId) {
      this.deleteMediaImage(imageId)
    },
    onBack() {
      this.$router.push({ name: 'CompaniesMediaLibraries', params: { company_uuid: this.companyUuid }})
    },
    changeStoreUuids(storeUuids) {
      this.storeUuids = storeUuids
    }
  }
}
</script>

<style lang="scss" scoped>
.fields {
  margin-top: 32px;
  &__field {
    &:not(:first-child) {
      margin-top: 32px;
    }
  }
  &__checkboxes {
    display: flex;
    gap: 32px;
    margin: 8px 0 16px;
  }
  &__tooltip {
    font-weight: normal;
    font-size: 12px;
    line-height: 1.4;
    white-space: pre-line;
  }
}
.selectStores {
  margin-top: 16px;
  padding: 24px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  &__checkbox {
    margin-bottom: 16px;
  }
  &__selectCategory {
    margin-bottom: 24px;
    &__error {
      margin-top: 16px;
    }
  }
}
.fontBold {
  font-weight: bold;
}
.underline {
  text-decoration: underline;
}
</style>
